import React from 'react'
import { ProductType } from "@/types";
import { ProductWarrantyVariant } from "@/types/Product";
import { truthy, truncateFloatsOnString } from '@/helpers'

type dataParseType = (s: any) => string

// remove Html Tag injection as text
export const removeTags: dataParseType =
  (str) => typeof str === 'string' ? str.replace(/(<([^>]+)>)/ig, '') : '';

export const digestData: dataParseType = (str) => (
  truncateFloatsOnString(
    removeTags(str)
  )
)
export type ProductAccordionChunk = {
  title: string,
  description: string | React.ReactNode,
}
export type AccordionInnerDataType = {
  section: string
  content: Array<ProductAccordionChunk>
}
export type AccordionDataType = {
  category: string,
  details: AccordionInnerDataType[]
}

type accordionParserType = (p: ProductType, sku: string, s: ProductWarrantyVariant) => AccordionDataType
type accordionDataParserType = (p: ProductType, sku: string) => AccordionDataType[]
type dimensionsArrayType = Array<{ key: string, title: string }>


const dimensionIsPresent = (variantFromWarranty) => ({ key }) => !!variantFromWarranty?.[key]
const dimensionToAccordionData = (variantFromWarranty) => ({ title, key }) => {
  const rawData = variantFromWarranty?.[key] || ''
  const data = digestData(rawData)
  return ({
    title,
    description: data,
  })
}

const dimensionArrayParser = (arr: dimensionsArrayType, variant: ProductWarrantyVariant) => (
  arr
    .filter(dimensionIsPresent(variant))
    .map(dimensionToAccordionData(variant))
)

const overAllProductInfoDimensions: dimensionsArrayType = [
  { key: 'primary_color', title: 'Color' },
  { key: 'materials', title: 'Made of' },
  { key: 'made_in', title: 'Made in' },
  { key: 'artist', title: 'Artist Name' },
  { key: 'bulb_included', title: 'Bulb Included' },
  { key: 'frame_details', title: 'Frame Details' },
  { key: 'please_note', title: 'Please Note' },
  { key: 'construction', title: 'Construction' },
  { key: 'chain_length', title: 'Chain Length' },
  { key: 'rod_length', title: 'Rod Length' },
  { key: 'bulb_watts', title: 'Wattage' },
  { key: 'care', title: 'Care Instructions' },
  { key: 'primary_finish', title: 'Finish' },
]

const getDescriptionSection: accordionParserType = (product, sku, variant) => {
  return {
    category: 'Description',
    details: [
      {
        content: [{
          title: 'Introduction',
          description: digestData(product?.meta_description)
        }, {
          title: 'SKU',
          description: sku
        }],
        section: "About this product",
      },
      {
        content: dimensionArrayParser(overAllProductInfoDimensions, variant),
        section: "Overall product information"
      }
    ]
  }
}


const dimensionsVariants: dimensionsArrayType = [
  { key: 'dimensions', title: 'Dimensions' },
  { key: 'weight', title: 'Weight' },
  { key: 'arm_height', title: 'Arm height' },
  { key: 'width', title: 'Width' },
  { key: 'height', title: 'Height' },
  { key: 'depth', title: 'Depth' },
  { key: 'bedding_size', title: 'Bedding size' },
  { key: 'rug_size', title: 'Rug size' },
  { key: 'pillowcase_size', title: 'Pillowcase size' },
  { key: 'clearance_height', title: 'Clearance height' },
  { key: 'distance_between_arms', title: 'Distance between arms' },
  { key: 'leg_height', title: 'Leg height' },
  { key: 'seat_depth', title: 'Seat depth' },
  { key: 'seat_height', title: 'Seat height' },
  { key: 'backplate_dimensions', title: 'Backplate dimensions' },
  { key: 'base_dimensions', title: 'Base dimensions' },
  { key: 'canopy_dimensions', title: 'Canopy dimensions' },
  { key: 'chain_length', title: 'Chain length' },
  { key: 'rod_length', title: 'Rod length' },
  { key: 'cord_length', title: 'Cord length' },
  { key: 'shade_dims', title: 'Shade dims' },
  { key: 'pile_height', title: 'Pile height' },
]

const getDimensionsAndWeight: accordionParserType = (p, sku, variant) => ({
  category: "Dimension & Weight",
  details: [{
    content: dimensionArrayParser(dimensionsVariants, variant),
    section: "Overall product information"
  }]
})
/**
 is_entryway: "False"
 is_free_shipping: "False"
 is_white_glove: Optional(Boolean),
 is_entryway: Optional(Boolean),
 is_returnable
 */

const returnableWording = `This item is returnable within 30 days of delivery. Restocking and return shipping fees will apply. See our Return Policy to `
const nonReturnableWording = `This item is non-returnable. See our Return Policy to `
// const nonReturnableWording = `This item is non-returnable. See our Return Policy for <a data-toggle="modal" data-target="#entryWayModal" onClick="location.href='/basket/homeDelivery.do?type=entryWay';" >details</a>.`

const getShippingAndReturn: accordionParserType = (p, sku, variant) => ({
  "category": "Shipping & Return",
  "details": [
    {
      "section": "Delivery",
      "content": [
        ...(variant.is_white_glove ? [{
          "title": "WHITE GLOVE",
          "description": React.createElement(
            "p",
            null, 
            "Additional shipping fee for delivery inside your home by our White Glove service partners. ",
            learnMoreAnchor('/faq/#shipping-delivery')
            )
        }] : []),
        ...(variant.is_entryway ? [{
          "title": "HOME DELIVERY",
          "description": React.createElement(
            "p",
            null,
            "Additional shipping fee applies. ",
            learnMoreAnchor('/faq/#shipping-delivery')
          )
            
        }] : []),
        ...(!variant.is_entryway && !variant.is_white_glove ? [{
          "title": "STANDARD DELIVERY",
          "description": React.createElement(
            "p",
            null,
            learnMoreAnchor('/faq/#shipping-delivery')
          )
        }]: []),
        {
          "title": "Returns",
          "description": React.createElement(
            'p', 
            null,
            truthy(variant?.is_returnable) ? returnableWording : nonReturnableWording,
            ' ',
            learnMoreAnchor('/faq/#returns-exchange'),
            '.'
          )
        }
      ]
    }
  ]
}
)

export const accordionDataParser: accordionDataParserType = (product, sku) => {
  try {
    const variantFromWarranty = product.warranty.variants?.[sku]
    const Description: AccordionDataType = getDescriptionSection(product, sku, variantFromWarranty)
    const DimensionAndWeight: AccordionDataType = getDimensionsAndWeight(product, sku, variantFromWarranty)
    const shippingAndReturn = getShippingAndReturn(product, sku, variantFromWarranty)
    
    return [Description, DimensionAndWeight, shippingAndReturn]  
  } catch {
    return []
  }
}

/**
 * Special workaround such that <a> is clickable
 */
function learnMoreAnchor (dest: string): React.ReactNode {
  return React.createElement(
    'a', {
      href: dest,
      onClick: () => {
        location.href = dest
      }
    }, 'Learn more')
}